import React from "react";
import { Link } from "react-router-dom";
import { PageHeader, cardStyle, pageStyle } from "../../components/ui";
import useNavModules from "../../components/NavV2/useNavModules";

// One line under each setup page's name.
const BLURBS = {
  "admin-setup": "Categories, services, service types and providers",
  branding: "Icons, offers and coupons",
  "hr-settings": "Leave types, shifts, approval flows, payroll",
  "inventory-settings": "Costing, warehouses, purchasing, categories",
  "dms-setup": "Models, roles, workshop and sales settings",
  "proc-setup": "Procurement rules and categories",
  "fin-setup": "Ledgers, tax, numbering and approvals",
  "fin-erp": "Connector to your ERP",
  "expense-management-settings": "Branches and cost heads",
};
const BUSINESS = new Set(["admin-setup", "branding"]);

const tile = {
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
  padding: "14px 16px",
  border: "1px solid #eceef2",
  borderRadius: 12,
  background: "#fff",
  textDecoration: "none",
  color: "#1f2937",
};
const iconBox = { width: 36, height: 36, borderRadius: 9, display: "grid", placeItems: "center", background: "rgba(194,24,91,0.08)", color: "#C2185B", flexShrink: 0 };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 };
const secTitle = { fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af", margin: "18px 0 8px" };

function Choice({ label, options, value, onChange }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2" style={{ marginBottom: 10 }}>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: "#374151", minWidth: 190 }}>{label}</span>
      <div className="btn-group" role="group" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`btn btn-sm ${value === o.value ? "btn-dark" : "btn-outline-secondary"}`}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Settings hub: every setup page this person may open, in one place (they used
 * to sit at the bottom of each module's menu), plus the menu preferences.
 * The list is the same permission-filtered one the menu uses.
 */
export default function SettingsHub() {
  const { settings, items, prefs } = useNavModules();
  const business = settings.filter((s) => BUSINESS.has(s.key));
  const moduleSetups = settings.filter((s) => !BUSINESS.has(s.key));
  const hasDealership = items.some((i) => i.key.startsWith("dms-"));

  const renderTiles = (list) => (
    <div style={grid}>
      {list.map((item) => {
        const Icon = item.icon;
        return (
          <Link key={item.key} to={item.to} style={tile} className="settings-hub-tile">
            <span style={iconBox}>{Icon ? <Icon size={20} /> : null}</span>
            <span>
              <b style={{ display: "block", fontSize: 14 }}>{item.label}</b>
              <small style={{ color: "#6b7280" }}>{BLURBS[item.key] || ""}</small>
            </span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <div style={pageStyle}>
      <PageHeader title="Settings" subtitle="Every setup page you can open, in one place" />

      {business.length > 0 && (
        <>
          <div style={secTitle}>Business</div>
          {renderTiles(business)}
        </>
      )}
      {moduleSetups.length > 0 && (
        <>
          <div style={secTitle}>Modules</div>
          {renderTiles(moduleSetups)}
        </>
      )}
      {settings.length === 0 && <div style={{ color: "#6b7280", fontSize: 14 }}>There are no setup pages for your role.</div>}

      <div style={secTitle}>Menu</div>
      <div style={{ ...cardStyle, padding: "16px 18px" }}>
        <Choice
          label="Menu style"
          value={prefs.version}
          onChange={prefs.setVersion}
          options={[
            { value: "v2", label: "New menu" },
            { value: "v1", label: "Old menu" },
          ]}
        />
        {hasDealership && prefs.version === "v2" && (
          <Choice
            label="Dealership in the menu"
            value={prefs.splitDealership ? "split" : "one"}
            onChange={(v) => prefs.setSplitDealership(v === "split")}
            options={[
              { value: "split", label: "Vehicle Sales + After-sales" },
              { value: "one", label: "One Dealership" },
            ]}
          />
        )}
        <div style={{ fontSize: 12.5, color: "#6b7280" }}>Saved on this browser only.</div>
      </div>
    </div>
  );
}
